import { FunctionsHttpError } from '@supabase/supabase-js';
import { bareReasonSentence, isBareReason } from '../../../supabase/functions/_shared/upstreamError';

/**
 * errsay (founder, 24 Oct 2026, on a TDS toast that read only "Unauthorized"): a reply that is only a
 * status word is never shown as the reason. Our own function's 401/403 is about the reader's Karr login;
 * any other status means a service behind the function refused, and the shared sentence says what that
 * status means and what to do.
 */
function sayBare(word: string, status: number | undefined): string {
    if (status === 401) {
        return 'Karr did not accept your sign-in for this step; it has probably expired. Sign out and back in, then try again.';
    }
    if (status === 403) {
        return 'Karr did not allow this step for your login. Ask the business owner to give you access, then try again.';
    }
    return bareReasonSentence({ status, message: word });
}

/**
 * Turn a Supabase `functions.invoke()` error into a message that says WHAT went
 * wrong. `invoke()` surfaces a generic "Edge Function returned a non-2xx status
 * code" and hides the real reason inside `error.context` (the Response). Every
 * Karr edge function returns `{ ok:false, error:"<human reason>" }` with a 4xx/5xx
 * status, so this reads that body and returns the actual cause — never a vague
 * status string. Use it in every `functions.invoke` catch/error branch:
 *
 *   const { data, error } = await supabase.functions.invoke('fn', { body });
 *   if (error) { toast.error('…', { description: await edgeErrorMessage(error) }); return; }
 */
export async function edgeErrorMessage(
    error: unknown,
    fallback = 'Something went wrong. Please try again.',
): Promise<string> {
    if (error instanceof FunctionsHttpError && error.context) {
        const res = error.context as Response;
        // Preferred: our edge functions' JSON body { error } / { message }.
        let wasJson = false;
        try {
            const body = await res.clone().json();
            wasJson = true;
            const msg = body?.error ?? body?.message;
            // NOTE: `String(msg)` on an object is "[object Object]", and an edge
            // function CAN put one there — `signature-link` and `fs-sign` both
            // handed over a whole ClientPortalError, so every failure on those
            // surfaces read "[object Object]" (25 Aug 2026). Both are fixed and
            // a gate stops a third, but this is the reader every edge call goes
            // through, so it stops being possible here rather than one function
            // at a time.
            const said = typeof msg === 'string'
                ? msg
                : (msg && typeof msg === 'object'
                    ? [(msg as Record<string, unknown>).message,
                        (msg as Record<string, unknown>).error]
                        .find((v) => typeof v === 'string' && v.trim())
                    : undefined);
            if (typeof said === 'string' && said.trim()) return isBareReason(said) ? sayBare(said, res.status) : said;
        } catch { /* body wasn't JSON — fall through */ }
        // Fallback: a short plain-text body (e.g. a gateway/runtime error).
        // Only when the body was NOT json: a json body with nothing sayable in
        // it would otherwise be printed at a person as raw JSON.
        if (!wasJson) {
            try {
                const txt = (await res.text()).trim();
                if (txt && txt.length <= 500) return isBareReason(txt) ? sayBare(txt, res.status) : txt;
            } catch { /* ignore */ }
        }
        // Last resort for an HTTP error with no readable body: name the status.
        if (typeof res.status === 'number') return sayBare('', res.status);
    }
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === 'string' && error) return error;
    return fallback;
}

/** Karr's sentence for a database that is out of reach for a moment (schemawait). */
const DB_WAKING_SAY = "Karr's database was out of reach for a moment. Nothing was lost or changed, and Karr tries again on its own.";

/**
 * Codes where Postgres's own words help nobody, so Karr speaks INSTEAD of it.
 *
 * The distinction from `PG_CODE_HELP` below is the point. There, the raw
 * message names the column or constraint at fault and is worth keeping — Karr
 * only adds what it means. Here the raw message is pure plumbing, and appending
 * an explanation to it just makes a frightening line longer.
 *
 * The one that prompted this reached a consultant's home screen verbatim:
 *
 *     Could not load the client roster
 *     canceling statement due to statement timeout
 *
 * It was a transient database hiccup — the query behind it answers in 45 ms on
 * that org — but a reader has no way to know that, and "canceling statement"
 * reads as though something was undone. Every code below is environmental:
 * nothing the user did caused it, nothing was changed, and trying again really
 * is the right advice.
 */
const PG_CODE_INSTEAD: Record<string, string> = {
    // 57014 query_canceled — in Supabase, nearly always the statement timeout.
    '57014': 'That took longer than the database allows, so it was stopped. Nothing was changed. This is usually temporary — try again in a moment.',
    // 53300 too_many_connections
    '53300': 'The database is busy and would not take another connection. Nothing was changed — try again in a moment.',
    // 57P01 admin_shutdown · 57P03 cannot_connect_now
    '57P01': 'The database restarted while this was running. Nothing was changed — try again.',
    '57P03': 'The database is not accepting work yet. Try again in a moment.',
    // 08006 connection_failure · 08003 connection_does_not_exist
    '08006': 'Karr could not reach the database. Check your connection and try again.',
    '08003': 'The connection to the database dropped. Try again.',
    // PostgREST's own: the gateway is up but has lost Postgres for a moment
    // (a restart, a maintenance window, every connection taken). PGRST002 is
    // "Could not query the database for the schema cache. Retrying.", which
    // reached the founder's screen as a full-page "We couldn't load your
    // businesses" on 30 Oct 2026 (schemawait).
    'PGRST000': DB_WAKING_SAY,
    'PGRST001': DB_WAKING_SAY,
    'PGRST002': DB_WAKING_SAY,
};

/** The timeout's own words, for when no code travels with the error. */
const TIMEOUT_TEXT = /canceling statement|statement timeout|query_canceled/i;

/** PostgREST's words for "I cannot reach Postgres right now", when no code travels. */
const DB_WAKING_TEXT = /schema cache\. retrying|could not query the database for the schema cache|could not connect with the database|database client error/i;

/** The codes that mean the database itself is out of reach for a moment. */
const DB_WAKING_CODES = new Set(['PGRST000', 'PGRST001', 'PGRST002', '57P01', '57P03', '53300', '08006', '08003']);

/**
 * Is the database itself out of reach for a moment (restarting, every
 * connection taken), as opposed to refusing THIS request?
 *
 * The boot read and the OrgsUnavailable panel wait this out on their own
 * instead of showing an error with a button (schemawait, 30 Oct 2026). A
 * string works too, because the panel only has the message dbErrorMessage()
 * made, and that message is Karr's sentence for these codes.
 */
export function isDatabaseWaking(error: unknown): boolean {
    if (!error) return false;
    if (typeof error === 'string') {
        return DB_WAKING_TEXT.test(error)
            || [...DB_WAKING_CODES].some((c) => error.includes(PG_CODE_INSTEAD[c]));
    }
    const e = error as { code?: unknown; message?: unknown };
    if (typeof e.code === 'string' && DB_WAKING_CODES.has(e.code)) return true;
    return typeof e.message === 'string' && DB_WAKING_TEXT.test(e.message);
}

/**
 * Is this the kind of database error that fixes itself?
 *
 * True for a timeout, a connection wobble or a restart. **False for a missing
 * column, a constraint or a permission** — those fail identically forever, and
 * retrying one only delays the moment a human finds out.
 */
export function isTransientDbError(error: unknown): boolean {
    if (!error) return false;
    const e = error as { code?: string; message?: string };
    if (e.code && e.code in PG_CODE_INSTEAD) return true;
    return TIMEOUT_TEXT.test(String(e.message ?? '')) || DB_WAKING_TEXT.test(String(e.message ?? ''));
}

/**
 * The browsers' words for "the request never got an HTTP answer at all":
 * Chrome says `Failed to fetch`, Firefox `NetworkError when attempting to
 * fetch resource.`, Safari `Load failed` (undici's `fetch failed` covers
 * tests). None of these is a server speaking — DNS, TLS, an offline radio,
 * a dropped connection.
 */
const NETWORK_FAILURE_TEXT =
    /failed to fetch|networkerror when attempting|load failed|network request failed|fetch failed/i;

/**
 * Did this request die at the NETWORK layer — no HTTP answer at all?
 *
 * supabase-js surfaces that failure two different ways, and both land here:
 * auth calls THROW the browser's TypeError, while PostgREST reads catch it and
 * RETURN a PostgrestError-shaped object whose `message` is the stringified
 * TypeError ("TypeError: Failed to fetch") with the stack in `details`. A
 * string works too, so a message built by dbErrorMessage() can be asked as
 * well.
 *
 * The answer decides whether retrying can help. A network failure is the one
 * kind of failure that fixes itself — a real API answer (401, RLS, a missing
 * column) fails identically forever, and retrying one only delays the moment
 * a human finds out. That is why the boot read and the OrgsUnavailable panel
 * retry on this, and only this.
 */
export function isNetworkFailure(error: unknown): boolean {
    if (!error) return false;
    if (typeof error === 'string') return NETWORK_FAILURE_TEXT.test(error);
    const e = error as { message?: unknown };
    return typeof e.message === 'string' && NETWORK_FAILURE_TEXT.test(e.message);
}

/** What Postgres told us, in words a person can act on. */
const PG_CODE_HELP: Record<string, string> = {
    // The founder applies migrations by hand, so "column doesn't exist" almost
    // always means the live database is behind the app — not a coding mistake.
    '42703': 'The live database is missing a column this screen writes, so the migration that adds it has not been applied yet.',
    '42P01': 'The live database is missing a table this screen uses, so the migration that creates it has not been applied yet.',
    '23505': 'Something with this value already exists.',
    '23503': 'This refers to a record that no longer exists.',
    '23502': 'A required field was left empty.',
    '42501': 'Your role is not allowed to do this.',
};

/**
 * `42501` is raised by two very different things.
 *
 * Postgres raises it when an RLS policy refuses a row, and that wording ("new
 * row violates row-level security policy for table …") is useless to a person,
 * so the help sentence above is exactly what they need.
 *
 * But Karr also raises it from its own guards with `USING ERRCODE = '42501'`,
 * and those already say what happened in a full sentence. Appending "Your role
 * is not allowed to do this." to one of those sends the reader hunting for a
 * permissions problem they do not have: the seat cap said "Seat limit reached:
 * your plan allows 2 member(s). Cancel a pending invitation, or upgrade…" and
 * the banner then blamed the reader's role (Collaboration, 25 Aug 2026). So the
 * generic help is added only when the message reads like a bare refusal.
 */
const BARE_PERMISSION_TEXT = /row-level security|permission denied|insufficient privilege|not authori[sz]ed/i;

/** The help sentence for a code, or nothing when the message already explains itself. */
function helpFor(code: string | undefined, said: string): string | undefined {
    if (!code) return undefined;
    if (code === '42501' && said && !BARE_PERMISSION_TEXT.test(said)) return undefined;
    return PG_CODE_HELP[code];
}

/**
 * Turn a Supabase database error into a message that says WHAT went wrong.
 *
 * `supabase-js` returns a **PostgrestError — a plain object, not an `Error`** —
 * so the common `err instanceof Error ? err.message : 'Unknown error'` pattern
 * throws the real cause away and shows "Unknown error". That is how a missing
 * `salary_structures.gratuity_applicable` column reached a user as nothing at
 * all. Use this in every `.from(...)` catch branch:
 *
 *   catch (err) { toast.error('Could not save', { description: dbErrorMessage(err) }); }
 */
export function dbErrorMessage(
    error: unknown,
    fallback = 'Something went wrong. Please try again.',
): string {
    // Environmental failures speak in Karr's words INSTEAD of Postgres's, and
    // this is checked FIRST — before the `instanceof Error` shortcut below —
    // because a caller that wrapped the PostgrestError in an `Error` would
    // otherwise leak "canceling statement due to statement timeout" straight to
    // the screen, which is exactly how it reached one.
    {
        const e = (error ?? {}) as { code?: string; message?: string };
        const instead = (e.code && PG_CODE_INSTEAD[e.code])
            || (TIMEOUT_TEXT.test(String(e.message ?? (typeof error === 'string' ? error : '')))
                ? PG_CODE_INSTEAD['57014'] : undefined)
            || (DB_WAKING_TEXT.test(String(e.message ?? (typeof error === 'string' ? error : '')))
                ? DB_WAKING_SAY : undefined);
        if (instead) return instead;
    }
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === 'string' && error) return error;
    if (error && typeof error === 'object') {
        const e = error as { message?: string; details?: string; hint?: string; code?: string };
        // `details` usually names the offending column or constraint — keep it.
        const said = [e.message, e.details].filter(Boolean).join(' — ');
        const help = helpFor(e.code, said);
        const parts = [said || undefined, help, e.hint || undefined].filter(Boolean);
        if (parts.length) return parts.join(' ');
        if (e.code) return `Database error ${e.code}.`;
    }
    return fallback;
}
